import React, { useEffect } from 'react';
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet-async";

export default function PrivacyPolicy() {
  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  const primaryColor = "#684df4";

  const onHover = (e) => {
    e.currentTarget.style.background = primaryColor;
    e.currentTarget.style.color = '#ffffff';
    e.currentTarget.style.transform = 'translateY(-8px) scale(1.025)';
    e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.18)';
    e.currentTarget.style.borderColor = primaryColor;
    const icon = e.currentTarget.querySelector('i');
    if (icon) icon.style.color = '#ffffff';
  };

  const onLeave = (e) => {
    e.currentTarget.style.background = 'white';
    e.currentTarget.style.color = 'inherit';
    e.currentTarget.style.transform = 'translateY(0) scale(1)';
    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
    e.currentTarget.style.borderColor = '#a0a0a0';
    const icon = e.currentTarget.querySelector('i');
    if (icon) icon.style.color = primaryColor;
  };

  return (
    <div>
      <Helmet>
        <title>Privacy Policy - Technic Mentors</title>
        <link rel="canonical" href="https://technicmentors.com/privacy-policy" />
      </Helmet>

      {/* Hero Header */}
      <div className="allHeaderBg">
        <div className="allHeader-overlay d-flex align-items-center justify-content-center">
          <div>
            <h1 className="head text-center text-white animate__animated animate__zoomIn allHeaderP">
              Privacy Policy
            </h1>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="container mt-5" data-aos="fade-up">
        <div className="section-title text-center">
          <h2>Your Privacy Matters</h2>
          <h3><span>Technic Mentors Privacy Policy</span></h3>
        </div>
           <strong>Effective Date: 3rd April 2026.</strong>
        <p style={{ textAlign: "justify" }}>
          Welcome to <strong>Technic Mentors</strong> . We are committed to protecting your
          privacy and ensuring that your personal information is handled in a safe and responsible manner.
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
          visit <a href="https://technicmentors.com" target="_blank" rel="noopener" style={{ textDecoration: "none", color: primaryColor }}>https://technicmentors.com</a> and use our services.
        </p>
        <p style={{ textAlign: "justify" }}>
          By accessing or using our website, you agree to the terms of this Privacy Policy.{" "}
         
        </p>
      </div>

      {/* Quick Overview Cards */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="row g-4">
          {[
            { icon: "fa-shield-halved", title: "Data Protection" },
            { icon: "fa-user-lock", title: "Your Privacy Rights" },
            { icon: "fa-cookie-bite", title: "Cookie Policy" },
            { icon: "fa-lock", title: "Secure & Encrypted" },
            { icon: "fa-eye-slash", title: "No Data Selling" },
            { icon: "fa-scale-balanced", title: "Legal Compliance" },
          ].map((item, i) => (
            <div className="col-md-4" key={i}>
              <div
                className="card shadow-sm h-100"
                style={{
                  transition: 'all 0.28s ease',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #a0a0a0',
                  backgroundColor: '#ffffff',
                }}
                onMouseEnter={onHover}
                onMouseLeave={onLeave}
              >
                <div className="card-body d-flex align-items-center p-4">
                  <i
                    className={`fa-solid ${item.icon} fa-lg me-3`}
                    style={{ color: primaryColor, transition: 'color 0.28s ease' }}
                  ></i>
                  <p className="card-title mb-0 fw-medium">{item.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 2 - Information We Collect */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>Information <span>We Collect</span></h3>
        </div>
        <div className="row g-4">
          {[
            {
              icon: "fa-user",
              title: "Personal Information",
              items: ["Full name", "Email address", "Phone number", "Company or organization name", "Information provided via contact forms"]
            },
            {
              icon: "fa-desktop",
              title: "Technical & Usage Data",
              items: ["IP address", "Browser type and version", "Device type", "Pages visited and time spent", "Referring URLs"]
            },
            {
              icon: "fa-cookie-bite",
              title: "Cookies & Tracking",
              items: ["Improve website functionality", "Analyze traffic and user behavior", "Enhance user experience", "Controllable via browser settings"]
            },
          ].map((item, i) => (
            <div className="col-md-4" key={i}>
              <div
                className="card shadow-sm h-100"
                style={{
                  transition: 'all 0.28s ease',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #a0a0a0',
                  backgroundColor: '#ffffff',
                }}
                onMouseEnter={onHover}
                onMouseLeave={onLeave}
              >
                <div className="card-body p-4">
                  <i
                    className={`fa-solid ${item.icon} fa-2x mb-3`}
                    style={{ color: primaryColor, transition: 'color 0.28s ease' }}
                  ></i>
                  <h5 className="card-title mb-3">{item.title}</h5>
                  <ul className="list-unstyled mb-0">
                    {item.items.map((point, j) => (
                      <li key={j} className="mb-1 small">
                        <i className="fa-solid fa-circle-check me-2" style={{ color: primaryColor, fontSize: '0.75rem' }}></i>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 3 - How We Use */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>How We <span>Use Your Information</span></h3>
        </div>
        <div className="row g-4">
          {[
            { icon: "fa-headset", text: "Respond to inquiries and provide support" },
            { icon: "fa-gears", text: "Deliver and improve our services" },
            { icon: "fa-bell", text: "Communicate updates, offers, or important notices" },
            { icon: "fa-chart-bar", text: "Monitor and analyze website usage" },
            { icon: "fa-shield-halved", text: "Ensure security and prevent fraud" },
          ].map((item, i) => (
            <div className="col-md-6 col-lg-4" key={i}>
              <div
                className="card shadow-sm h-100 text-center p-4"
                style={{
                  transition: 'all 0.28s ease',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #a0a0a0',
                  backgroundColor: '#ffffff',
                }}
                onMouseEnter={onHover}
                onMouseLeave={onLeave}
              >
                <i
                  className={`fa-solid ${item.icon} fa-2x mb-3`}
                  style={{ color: primaryColor, transition: 'color 0.28s ease' }}
                ></i>
                <p className="mb-0 fw-medium">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 4 & 5 - Sharing + Security side by side */}
      <div className="container my-5" data-aos="fade-up">
        <div className="row align-items-start g-4">
          <div className="col-md-6">
            <div className="section-title text-start">
              <h3>Sharing & <span>Disclosure</span></h3>
            </div>
            <p style={{ textAlign: "justify" }}>
              We do <strong>not sell, rent, or trade</strong> your personal information. We may share your
              information only in limited circumstances:
            </p>
            <div className="d-flex flex-column gap-3">
              {[
                { icon: "fa-server", text: "Trusted service providers (hosting, analytics, email services)" },
                { icon: "fa-gavel", text: "Legal authorities when required by law" },
                { icon: "fa-handshake", text: "Business partners only when necessary for service delivery" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="card shadow-sm"
                  style={{
                    transition: 'all 0.28s ease',
                    borderRadius: '12px',
                    border: '1px solid #a0a0a0',
                    backgroundColor: '#ffffff',
                  }}
                  onMouseEnter={onHover}
                  onMouseLeave={onLeave}
                >
                  <div className="card-body d-flex align-items-center p-3">
                    <i className={`fa-solid ${item.icon} fa-lg me-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                    <p className="mb-0 small">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-md-6">
            <div className="section-title text-start">
              <h3>Data <span>Security</span></h3>
            </div>
            <p style={{ textAlign: "justify" }}>
              We implement appropriate technical and organizational measures to protect your data.
            </p>
            <div className="d-flex flex-column gap-3">
              {[
                { icon: "fa-lock", text: "Secure servers and encrypted communication (HTTPS)" },
                { icon: "fa-user-lock", text: "Access control and authentication systems" },
                { icon: "fa-magnifying-glass", text: "Regular monitoring and security updates" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="card shadow-sm"
                  style={{
                    transition: 'all 0.28s ease',
                    borderRadius: '12px',
                    border: '1px solid #a0a0a0',
                    backgroundColor: '#ffffff',
                  }}
                  onMouseEnter={onHover}
                  onMouseLeave={onLeave}
                >
                  <div className="card-body d-flex align-items-center p-3">
                    <i className={`fa-solid ${item.icon} fa-lg me-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                    <p className="mb-0 small">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section 7 - Your Rights */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>Your <span>Rights</span></h3>
        </div>
        <div className="row g-4">
          {[
            { icon: "fa-eye", text: "Access your personal data" },
            { icon: "fa-pen-to-square", text: "Request correction of inaccurate information" },
            { icon: "fa-trash-can", text: "Request deletion of your data" },
            { icon: "fa-hand", text: "Withdraw consent for data processing" },
          ].map((item, i) => (
            <div className="col-md-6 col-lg-3" key={i}>
              <div
                className="card shadow-sm h-100 text-center p-4"
                style={{
                  transition: 'all 0.28s ease',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #a0a0a0',
                  backgroundColor: '#ffffff',
                }}
                onMouseEnter={onHover}
                onMouseLeave={onLeave}
              >
                <i
                  className={`fa-solid ${item.icon} fa-2x mb-3`}
                  style={{ color: primaryColor, transition: 'color 0.28s ease' }}
                ></i>
                <p className="mb-0 fw-medium">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-muted">
          To exercise any of these rights, please contact us at{" "}
          <a href="mailto:info@technicmentors.com" style={{ color: primaryColor, textDecoration: "none" }}>
            info@technicmentors.com
          </a>
        </p>
      </div>

      {/* Sections 8, 9, 10, 6 - Additional Policies */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>Additional <span>Policy Details</span></h3>
        </div>
        <div className="row g-4">
          {[
            {
              icon: "fa-arrow-up-right-from-square",
              title: "Third-Party Links",
              desc: "Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those websites."
            },
            {
              icon: "fa-child",
              title: "Children's Privacy",
              desc: "Our website and services are not intended for children under the age of 13. We do not knowingly collect personal information from children."
            },
            {
              icon: "fa-globe",
              title: "International Data Transfers",
              desc: "As Technic Mentors operates globally, your data may be processed in Pakistan and other regions where we operate. Appropriate safeguards are in place."
            },
            {
              icon: "fa-database",
              title: "Data Retention",
              desc: "We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy, comply with legal obligations, and resolve disputes."
            },
            {
              icon: "fa-rotate",
              title: "Policy Updates",
              desc: "We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Please review periodically."
            },
          ].map((item, i) => (
            <div className="col-md-6 col-lg-4" key={i}>
              <div
                className="card shadow-sm h-100"
                style={{
                  transition: 'all 0.28s ease',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #a0a0a0',
                  backgroundColor: '#ffffff',
                }}
                onMouseEnter={onHover}
                onMouseLeave={onLeave}
              >
                <div className="card-body text-center p-4">
                  <i
                    className={`fa-solid ${item.icon} fa-2x mb-3`}
                    style={{ color: primaryColor, transition: 'color 0.28s ease' }}
                  ></i>
                  <h5 className="card-title mb-3">{item.title}</h5>
                  <p className="card-text small">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Us */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div
          className="p-5 text-center"
          style={{
            background: "linear-gradient(135deg, rgba(104,77,244,0.08), rgba(104,77,244,0.03))",
            borderRadius: "16px",
            border: "1px solid rgba(104,77,244,0.2)",
          }}
        >
          <i className="fa-solid fa-envelope-open-text fa-3x mb-4" style={{ color: primaryColor }}></i>
          <div className="section-title text-center mb-3">
            <h3>Contact <span>Us</span></h3>
          </div>
          <p className="lead mb-4" style={{ color: "#555" }}>
            If you have any questions or concerns about this Privacy Policy, feel free to reach out.
          </p>
          <div className="row justify-content-center g-3">
       {[

  { icon: "fa-envelope", label: "Email", value: "info@technicmentors.com", href: "mailto:info@technicmentors.com" },
  { icon: "fa-phone", label: "Phone", value: "+92 311 1122144", href: "tel:+923111122144" },
].map((item, i) => (
  <div className="col-md-4" key={i}>
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <div
        className="card shadow-sm h-100 text-center p-4"
        style={{
          transition: 'all 0.28s ease',
          borderRadius: '12px',
          border: '1px solid #a0a0a0',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
        }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
      >
        <i className={`fa-solid ${item.icon} fa-2x mb-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
        <p className="fw-bold mb-1">{item.label}</p>
        <p style={{ fontSize: "0.9rem", marginBottom: 0 }}>{item.value}</p>
      </div>
    </a>
  </div>
))}
          </div>
        </div>
      </div>

    </div>
  );
}